import {
  Box,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { ClothingDataDB, CreditingType } from 'models'
import { useContext, useState } from 'react'
import ButtonWithLoading from './button_with_loading'
import { UnauthorisedError } from 'errors'
import { UserContext } from 'base'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enGB from 'date-fns/locale/en-GB'

interface ClothingDataFormProps {
  clothingData: ClothingDataDB
  onSubmit: (clothingData: ClothingDataDB) => Promise<void>
}

const ClothingDataForm = ({
  clothingData,
  onSubmit,
}: ClothingDataFormProps) => {
  const [customerFirstName, setCustomerFirstName] = useState<string>(
    clothingData.customer_first_name
  )
  const [customerEmail, setCustomerEmail] = useState<string>(
    clothingData.customer_email
  )
  const [creditingType, setCreditingType] = useState<CreditingType>(
    clothingData.crediting_type
  )
  const [collectionDate, setCollectionDate] = useState<Date>(
    clothingData.collection_date
  )

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const user = useContext(UserContext)

  const handleCustomerFirstName = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCustomerFirstName(event.target.value)
  }

  const handleCustomerEmail = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCustomerEmail(event.target.value)
  }

  const handleCreditingDate = (newDate: Date | null) => {
    if (newDate) setCollectionDate(newDate)
  }

  const handleCreditingType = (
    event: React.MouseEvent<HTMLElement>,
    newCreditingType: CreditingType | null
  ) => {
    if (newCreditingType !== null) {
      setCreditingType(newCreditingType)
    }
  }

  return (
    <Box
      component="form"
      onSubmit={async (event) => {
        setIsSaving(true)
        event.preventDefault()
        try {
          await onSubmit(
            ClothingDataDB.fromJson({
              ...clothingData,
              customer_first_name: customerFirstName,
              customer_email: customerEmail,
              crediting_type: creditingType,
              collection_date: collectionDate,
            })
          )
        } catch (e: any) {
          if (e instanceof UnauthorisedError) {
            user.setLoggedOut()
          }
        }
        setIsSaving(false)
      }}
    >
      <Stack spacing={3}>
        <TextField
          required
          label="Name"
          fullWidth
          onChange={handleCustomerFirstName}
          value={customerFirstName}
        />
        <TextField
          required
          inputProps={{ type: 'email' }}
          label="Email"
          fullWidth
          onChange={handleCustomerEmail}
          value={customerEmail}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <DatePicker
            label="Collection Date"
            disableFuture
            value={collectionDate}
            onChange={handleCreditingDate}
            renderInput={(params) => <TextField {...params} required />}
          />
        </LocalizationProvider>

        <ToggleButtonGroup
          exclusive
          fullWidth
          value={creditingType}
          onChange={handleCreditingType}
        >
          <ToggleButton value={CreditingType.TRADE_IN}>
            {CreditingType.TRADE_IN}
          </ToggleButton>
          <ToggleButton value={CreditingType.DONATION}>
            {CreditingType.DONATION}
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" justifyContent="right">
          <ButtonWithLoading
            type="submit"
            variant="contained"
            isLoading={isSaving}
          >
            Save
          </ButtonWithLoading>
        </Stack>
      </Stack>
    </Box>
  )
}

export default ClothingDataForm
